import { IUser } from '@aca-new/app/pages/user/shared/models/interfaces/user.interface';
import { UserService } from '@aca-new/app/pages/user/shared/service/user.service';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { IMyWindow } from '@aca-new/app/shared/interfaces/my-window.interface';
import { HttpService } from '@aca-new/app/shared/services/http-services/http.service';
import { WINDOW } from '@aca-new/app/shared/tokens/window.token';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { QimaOptionalType } from '@qima/ngx-qima';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

declare let SHOULD_INIT_CHAT_BOX: boolean;
declare let FRESH_CHAT_TOKEN: string;

interface IFcWidgetUser {
  data: {
    restoreId: string;
  };
  status: number;
}

interface IFcWidget {
  destroy(): void;
  init(config: Record<string, unknown>): void;
  isLoaded(): boolean;
  on(event: string, callback: (resp: QimaOptionalType<IFcWidgetUser>) => void): void;
  setTags(tags: string[]): void;
  user: {
    get(callback: (resp: QimaOptionalType<IFcWidgetUser>) => void): void;
    setProperties(properties: Record<string, unknown>): void;
  };
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class FreshChatService {
  public shouldInitChatBox: boolean = this._window.shouldInitChatBox ?? SHOULD_INIT_CHAT_BOX;
  public freshChatToken: string = this._window.freshChatToken ?? FRESH_CHAT_TOKEN;

  private _activeExternalId: string | null = null;

  public constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    @Inject(WINDOW) private readonly _window: IMyWindow,
    private readonly _httpService: HttpService,
    private readonly _userService: UserService
  ) {}

  public initChat(userData: IUser): void {
    if (!this.shouldInitChatBox) {
      return;
    }

    const tag = this._resolveTag();
    const fcWidget = this._getFcWidget();

    if (!fcWidget) {
      return;
    }

    const isWidgetLoaded = fcWidget.isLoaded();
    const isDifferentUser = this._activeExternalId !== null && this._activeExternalId !== userData.id;
    const hasStaleLoadedWidget = isWidgetLoaded && this._activeExternalId === null;
    const shouldInitialize = !isWidgetLoaded || isDifferentUser || hasStaleLoadedWidget;

    if (shouldInitialize && isWidgetLoaded) {
      fcWidget.destroy();
    }

    if (shouldInitialize) {
      this._initFreshChat(tag, userData);
      this._activeExternalId = userData.id;
    } else {
      fcWidget.setTags([tag]);
    }

    this._showWidget(true);
  }

  public clearFreshChat(): void {
    const fcWidget = this._getFcWidget();

    fcWidget?.destroy();
    this._activeExternalId = null;
    this._showWidget(false);
  }

  private _resolveTag(): string {
    if (this._window.location.host.includes('my.qima.cn')) {
      return 'ACACN';
    }

    return 'ACACOM';
  }

  private _getFcWidget(): IFcWidget | null {
    const windowRef = this._document.defaultView as (Window & { fcWidget?: IFcWidget }) | null;

    return windowRef?.fcWidget ?? null;
  }

  private _showWidget(isShow: boolean): void {
    const element = this._document.getElementsByClassName('custom_fc_frame')[0] as HTMLElement;

    if (!element) {
      return;
    }

    element.style.display = isShow ? 'block' : 'none';
  }

  private _initFreshChat(tag: string, userData: IUser): void {
    const [email] = [userData.email.split(';')[0]];
    const { login, firstName, lastName } = userData;
    const freshChatRestoreId = userData.restoreId;
    const fcWidget = this._getFcWidget();

    if (!fcWidget) {
      return;
    }

    const freshChatConfig: Record<string, unknown> = {
      token: this.freshChatToken,
      host: 'https://qima-operations.freshchat.com',
      tags: [tag],
      externalId: userData.id,
      restoreId: freshChatRestoreId || null,
    };

    if (tag === 'ACACN') {
      freshChatConfig.widgetUuid = '4730acd3-b198-40d3-bbef-61ed141992ab';
    }

    fcWidget.init(freshChatConfig);
    fcWidget.user.setProperties({
      email,
      firstName,
      lastName,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Login: login,
      plan: 'Pro',
      status: 'Active',
    });
    fcWidget.user.get((resp: QimaOptionalType<IFcWidgetUser>): void => {
      const status = resp?.status;

      if (status !== 200) {
        fcWidget.on('user:created', (resp: QimaOptionalType<IFcWidgetUser>): void => {
          const status = resp?.status;
          const data = resp?.data;

          if (status === 200 && data?.restoreId) {
            this._saveRestoreId$(data.restoreId, userData.id).pipe(untilDestroyed(this)).subscribe();
          }
        });
      }
    });
  }

  private _saveRestoreId$(restoreId: string, userId: string): Observable<void> {
    return this._httpService.httpClient.put<void>(`${SERVER_URL}/user/v2/${userId}/${restoreId}`, {}, { observe: 'body' }).pipe(
      map((): void => {
        this._userService.watchUserDataRefresh$().pipe(untilDestroyed(this)).subscribe();
      })
    );
  }
}
